<script>
import DiscountCodeComponent from './DiscountCodeComponent.vue';
import { computed, inject, onMounted, ref } from 'vue';
import Flicking from '@egjs/vue3-flicking';
import { Pagination } from '@egjs/flicking-plugins';
import { Arrow } from '@egjs/flicking-plugins';
import '@egjs/vue3-flicking/dist/flicking.css';
import { checkPoints } from '../strings/ServiceTableCheckPoints';
import { useAppDataStore } from '../stores/appDataStore';

export default {
  components: {
    DiscountCodeComponent,
    Flicking: Flicking,
  },
  props: {
    appDataS3Value: {
      type: String,
      required: true
    },
    tooltipContent: {
      type: Object,
      required: true
    },
    tooltipContentWl: {
      type: Object,
      required: true
    },
    preCalculatedAppPackageData: {
      type: Object,
      required: true
    },
    appThemeBrand: {
      type: String,
      default: 'default'
    }
  },
  setup(props, { emit }) {
    const globalDiscountAvailable = inject('discountAvailable');
    const flicking = ref(null);
    let currentSlideIndex = ref(0);
    const appDataStore = useAppDataStore();
    const appData = computed(() => appDataStore.appData);

    const goToSlide = (index) => {
      if (flicking.value && !flicking.value.animating) {
        flicking.value.moveTo(index);
      }
    }

    const updateCurrentSlideIndex = () => {
      currentSlideIndex.value = flicking.value.currentPanel.index;
    }

    const plugins = [
      new Pagination({
        type: 'bullet'
      }),
      new Arrow()
    ]

    onMounted(() => {
      goToSlide(1);
    })

    // Replaces the dot . with a comma , in prices
    const replaceDot = (value) => {
      return value.replace('.', ',');
    }

    // Checks if only the Basis package is available in step 5. Do not apply discount to this package
    const onlyBasisPackageAvailable = () => {
      return props.preCalculatedAppPackageData.basis && (!props.preCalculatedAppPackageData.komfort && !props.preCalculatedAppPackageData.premium);
    }

    const setSelectedPackage = (pack) => {
      let userSelectedPackage = props.preCalculatedAppPackageData[pack]
      let selectedPrice = userSelectedPackage.hasMonatlicherDiscount === true ? userSelectedPackage.discountedPrices.monatlicher : userSelectedPackage.priceFromApi
      appData.value.s5.userSelectedPrice = selectedPrice;
      appData.value.s5.value = pack;

      document.querySelector('.service-order-selection').scrollIntoView({ block: 'start',  behavior: 'smooth' });
    }

    return {
      replaceDot,
      onlyBasisPackageAvailable,
      setSelectedPackage,
      goToSlide,
      updateCurrentSlideIndex,
      globalDiscountAvailable,
      checkPoints,
      plugins,
      flicking,
      currentSlideIndex
    }
  }
}
</script>

<template>
  <!-- Discount code component is only available for corporatebenefits whitelabel and applies only to Komfort & Premium packages -->
  <DiscountCodeComponent v-if="!onlyBasisPackageAvailable() && appThemeBrand === 'corporatebenefits'" />
  <div id="mobile-service-table">
    <!-- Buttons to Control Slides -->
    <div class="plan-switcher">
      <div
        v-for="(planData, planTitle, index) in preCalculatedAppPackageData"
        :key="planTitle"
        class="plan"
        :class="{active: index === currentSlideIndex}"
        @click="goToSlide(index)"
      >
        {{ planTitle.replace('_', ' ') }}
      </div>
    </div>

    <Flicking ref="flicking" :options="{ circular: true }" :plugins="plugins" class="flicking-horizontal service-slider" @changed="updateCurrentSlideIndex">
      <div v-for="(planData, planTitle) in preCalculatedAppPackageData" :key="planTitle" class="service-slide">
        <div
          class="service-title mobile-row"
          :class="planTitle"
        >
          {{ planTitle.replace('_', ' ') }}
        </div>
        <div
          v-for="checkPoint in checkPoints"
          class="mobile-row"
          :class="{disabled: !checkPoint.packages.includes(planTitle)}"
        >
          <i class="icon" :class="checkPoint.packages.includes(planTitle) ? 'icon--check' : 'icon--not-checked'" />

          <span v-html="checkPoint.title"></span>

          <div v-tooltip="{html: true, content: appThemeBrand === 'default' ? tooltipContent[checkPoint.tooltipContent] : tooltipContentWl[checkPoint.tooltipContent]}"
               class="icon icon--help-second" />
        </div>
        <div
          v-if="!planData.hasEinmaligesDiscount"
          class="price custom-package-price">
          <span>{{ replaceDot(planData.einmaligesPrice.toFixed(2)) }}€</span> / Einmaliges  <br/>Einrichtungsentgelt
        </div>
        <div
          v-else
          class="price custom-package-price">
          <span class="price__current"> {{ replaceDot(planData.discountedPrices.einmaliges.toFixed(2)) }}€ </span> / Einmaliges  <br/>Einrichtungsentgelt
          <s>
            {{ replaceDot(planData.einmaligesPrice.toFixed(2)) }}€
          </s>
        </div>
        <div class="price-row">
          <div v-if="!planData.hasMonatlicherDiscount" class="price">
            <span> {{ replaceDot(planData.priceFromApi.toFixed(2)) }}€ </span>
          </div>
          <div v-else class="price discounted">
            <span class="price__current"> {{ replaceDot(planData.discountedPrices.monatlicher.toFixed(2)) }}€ </span>
            <s>
              {{ replaceDot(planData.priceFromApi.toFixed(2)) }}€
            </s>
          </div>
          <div class="price-title">
            Monatlich*
          </div>
        </div>
        <div class="-trans --footer">
          <button
            :class="{ active: userSelectedPackage === planTitle }"
            class="button button--angebot -no-margin"
            @click="setSelectedPackage(planTitle)"
          >
            Angebot auswählen
            <i v-if="userSelectedPackage === planTitle" class="icon icon--check" />
            <i v-else class="fa-solid fa-plus" />
          </button>
        </div>
      </div>

      <template #viewport>
        <div class="slider-controls">
          <span class="flicking-arrow-prev is-circle">
            <i class="icon icon--left-arrow"></i>
          </span>
          <div class="flicking-pagination"></div>
          <span class="flicking-arrow-next is-circle">
            <i class="icon icon--right-arrow"></i>
          </span>
        </div>
      </template>

    </Flicking>
  </div>
</template>
<style lang="scss">
@import '~brandVariables/variables.scss';
@media screen and (max-width: 767px) {
  .root .table_wrap {
    max-width: 100%;
  }
  .tooltip .tooltip-inner {
    background: $main-color;
    padding: 50px 15px 15px;
    position: relative;
    &:after {
      content: 'x';
      width: 30px;
      height: 30px;
      font-size: 20px;
      background: #fff;
      color: $main-color;
      position: absolute;
      top: 10px;
      right: 20px;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 50px;
    }
    ul {
      li {
        margin-bottom: 10px;
        font-size: 14px;
        line-height: 16px;
      }
    }
  }
  #mobile-service-table {
    position: relative;
    margin-bottom: 20px;

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    /* Pricing page container */
    .div {
      display: flex;
      flex-direction: column;
      align-items: center;
      max-width: 800px;
      margin: auto;
      background-color: #fff;
      box-shadow: 0 0 10px rgba(0, 5, 72, 0.1);
      border-radius: 8px;
      overflow: hidden;
    }
    .price__current {
      font-size: 36px;
      font-weight: 700;
      color: #EB003C;
    }

    /* Service slider */
    .service-slider {
      display: flex;
      overflow: hidden;
      width: 100%;
      margin-top: 14px;
      flex-direction: column;
    }

    .service-slide {
      flex: 0 0 85%;
      background: #fff;
      text-align: left;
      box-shadow: 0px 4px 19px 0px rgba(0, 0, 0, 0.15);
      transition: transform 0.3s ease-in-out;
      border-radius: 10px;
      margin: 5px;
      height: 100%;

      .price.custom-package-price {
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        color: $main-color;
        margin: 0 40px;
        padding: 12px 0;
        position: relative;
      }

      .mobile-row:not(:first-child).disabled {

        .icon.icon--help-second {
          color: #9fa1b8;
        }
      }
      .mobile-row:not(:first-child) {
        font-size: 14px;
        line-height: 16px;
        color: $main-color;
        display: flex;
        margin: 0 10px;
        padding: 12px 0;
        border-top: 1px solid rgba(119, 119, 119, 0.24);
        position: relative;

        .icon.icon--help-second {
          color: $main-color;
        }

        .has-tooltip.icon {
          top: 50%;
          transform: translate(-50%, -50%);
          right: -10px;
          z-index: 1000;
          &:after {
            font-size: 2.5rem;
          }
        }

        span {
          margin-left: 10px;
          max-width: 170px;
        }

        ul {
          margin-left: 38px;
          max-width: 160px;
        }

        .icon {
          display: flex;
          align-items: center;
          top: 50%;
          z-index: 1000;

          &:after {
            font-size: 2.2rem;
          }
        }
        .icon.icon--help-second.v-popper--has-tooltip {
          right: 2.5px;
        }

        .icon--check {
          color: #eb003c;
          &:after {
            font-size: 1.2rem;
          }
        }

        &.disabled {
          color: #9FA1B8;
        }
      }
    }

    .service-slide .mobile-row:first-child .has-tooltip.icon {
      top: 28px;
    }

    /* Service title and price */
    .service-title {
      font-size: 20px;
      text-transform: capitalize;
      font-weight: 700;
      background: $main-color;
      color: #fff;
      height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10px 10px 0 0;
    }

    .price-row {
      display: flex;
      justify-content: center;
      flex-direction: column;
      align-items: center;
      padding: 13px 0;
      background-color: rgba(235, 0, 60, 0.15);
    }

    .price-description {
      flex: 1;
      font-size: 16px;
      color: #777;
    }

    .price {
      font-size: 18px;
      font-weight: 700;
      padding: 10px 0 0;
      border-radius: 5px;
    }

    .price-title {
      font-size: 16px;
    }

    .discounted {
      color: #777;
    }


    .icon--check,
    .icon--not-checked {
      margin-right: 5px;
    }

    .slider-nav {
      display: flex;
      justify-content: space-between;
      flex-direction: inherit;
      margin-top: 20px;
      width: 100%;
      position: absolute;
      bottom: 0;
    }

    .slider-button {
      padding: 20px 20px;
      background-color: $main-color;
      color: #fff;
      border: none;
      cursor: pointer;
      border-radius: 5px;
      transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;
      position: relative;
      width: 29px;
      height: 29px;
      display: flex;
      border-radius: 50%;
      align-items: center;
      justify-content: center;

      i {
        color: #fff;
        position: unset;

        &.icon--right-arrow:after,
        &.icon--left-arrow:after {
          font-size: 14px;
        }
      }
    }

    .slider-button:hover {
      background-color: $main-color;
    }

    /* Footer button */
    .-trans {
      display: flex;
      justify-content: center;
      margin-top: 20px;
      margin-bottom: 10px;
    }

    .button--angebot {
      padding: 15px 30px;
      background-color: rgba(222, 255, 0, 1);
      border: none;
      cursor: pointer;
      border-radius: 10px;
      width: 93%;
      transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;

      .icon, .fa-solid {
        background: $main-color;
        border-radius: 20px;
        padding: 5px;
        display: inline-block;
        width: 25px;
        color: #fff;
      }
      &.active {
        .icon, .fa-solid {
          padding: 5px 7px 5px 5px;
        }
      }
    }

    .button--angebot::after {
      content: "\F13A";
    }
  }

  .flicking-pagination {
    gap: 1px;
    top: 10%;

    .flicking-pagination-bullet {
      height: 12px;
      width: 12px;
    }

  }

  .slider-controls {
    .flicking-arrow-next,
    .flicking-arrow-prev {
      height: 44px;
      width: 44px;
      &.is-circle {

        .icon {
          color: white;

          &.icon--left-arrow {
            top: 29.5%;
            left: 35%;
          }

          &.icon--right-arrow {
            top: 29.5%;
            left: 15%;
          }

          &::after {
            font-size: 1.5rem;
          }
        }
      }
    }
  }
}

/* Responsive styles */
@media (max-width: 600px) {
  .service-slider {
    flex-direction: column;
  }

  .slider-nav {
    flex-direction: column;
  }
}

</style>
